/**
 * @fileoverview The frame both photo screens sit in: one screen, nothing scrolling.
 *
 * The whole screen on a phone, as Edit plant, the chooser and the sharer are,
 * with the action pinned at the foot. The picture is the one thing inside that
 * gives way — it takes whatever height the fields leave it — so the fields
 * never have to scroll, and when the keyboard comes up for the note it is the
 * picture that shrinks. It used to be a small window with a fixed 240px
 * picture and five labelled fields stacked under it, which ran to 651px in a
 * 520px window with the upload button below the fold.
 *
 * Where: EditPhotoDialog, UploadPhotoDialog and ImageEditDialog sit in it.
 * API: none.
 * Settings: none.
 * Words (en/es): none; the title and the action are the caller's.
 * Footer (B111): the footer is a flex row; a lone child (an ActionRow) is made full width here, so its Cancel and action do not shrink to their words at 375px.
 *
 * @module PhotoDialogShell
 */

import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Spinner from '@/shared/components/ui/Spinner';
import { FramedPhoto } from '@/shared/components/ui/FramedPhoto';

/** The frame's title, body and foot. */
interface PhotoDialogShellProps {
  /** Whether the dialog is showing. */
  isOpen: boolean;
  /** Closes it (Escape, a tap outside, the ×). */
  onClose: () => void;
  /** The heading in the top bar. */
  title: string;
  /** The body: the picture and the fields. */
  children: React.ReactNode;
  /** Pinned below the body. Omitted where there is nothing to pin. */
  footer?: React.ReactNode;
}

/**
 * The full-screen (on a phone) dialog both photo screens are drawn in.
 *
 * @param props - PhotoDialogShellProps.
 */
export const PhotoDialogShell: React.FC<PhotoDialogShellProps> = ({
  isOpen, onClose, title, children, footer,
}) => (
  <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent
      scrollBody={false}
      aria-describedby={undefined}
      className="flex h-[100dvh] max-h-none flex-col gap-0 overflow-hidden rounded-none border-0 p-0 max-sm:left-0 max-sm:top-0 max-sm:translate-x-0 sm:h-[min(90dvh,720px)] sm:max-w-[440px] sm:rounded-lg sm:border"
    >
      <DialogHeader className="flex min-h-[3.5rem] shrink-0 justify-center border-b px-4 py-2 pr-14 text-left">
        <DialogTitle>{title}</DialogTitle>
      </DialogHeader>

      {/* Scrolls only if the type is made very large; at any ordinary size
          the picture gives way first. */}
      <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto p-4">
        {children}
      </div>

      {footer && (
        <div className="flex shrink-0 items-center gap-2 border-t px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] [&>*:only-child]:w-full">
          {footer}
        </div>
      )}
    </DialogContent>
  </Dialog>
);

/** What the picture shows, and what lies over it. */
interface PhotoPictureProps {
  /** The image to show; nothing is drawn until there is one. */
  src: string | null;
  /** What the picture is of, for a screen reader. */
  alt: string;
  /** Darkens the picture under a spinner while an errand runs. */
  busy?: boolean;
  /** Called when the image has loaded, or failed to: either way it is done. */
  onLoad?: () => void;
  /**
   * Drawn instead of the plain picture: the editor's turned and cropped
   * preview (ImageEditStage), which loads the picture itself. src and onLoad
   * are then unused.
   */
  picture?: React.ReactNode;
  /** Buttons laid over the picture's corners. */
  children?: React.ReactNode;
}

/**
 * The picture, filling the room the fields leave, with whatever acts on the
 * picture itself laid over its corners.
 *
 * @param props - PhotoPictureProps.
 */
export const PhotoPicture: React.FC<PhotoPictureProps> = ({ src, alt, busy, onLoad, picture, children }) => (
  <div className="relative min-h-[7.5rem] flex-1 overflow-hidden rounded-md">
    {picture ?? (src && (
      <FramedPhoto fit="contain" src={src} alt={alt} onLoad={onLoad} onError={onLoad} />
    ))}
    {busy && (
      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
        <Spinner size={48} color="hsl(var(--primary))" />
      </div>
    )}
    {children}
  </div>
);
